'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { buildBreadcrumbManager } from '@coveo/atomic-react';
import { useController } from '@lib/hooks/useController';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { Faders, XCircle } from 'phosphor-icons';
import { useCallback, useMemo, useState } from 'react';
import { c } from 'ui-components';
import { useEngine } from '../EngineContext';
import { MobileFilterDrawer } from '../MobileFilterDrawer';
import {
  buildSaleBundle,
  getNumericRangeLabel,
  resolveSaleLabel,
} from '../facetControllers';
import { clearOwnershipFilter } from '../utils/clearOwnershipFilter';
import { MobileFilterBarProps } from './MobileFilterBar.types';

const MAX_VISIBLE_CHIPS = 3;

export const MobileFilterBar = ({ className }: MobileFilterBarProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const getLabel = (key: string, fallback: string) =>
    searchLabelsMicrocopy?.find((m) => m.key === key)?.value ?? fallback;

  const saleBundle = useMemo(
    () => buildSaleBundle(searchValuesMicrocopy),
    [searchValuesMicrocopy]
  );

  const eng = useEngine();
  const controller = useMemo(() => buildBreadcrumbManager(eng), [eng]);
  const state = useController(controller);

  // Build breadcrumb items from Coveo state
  const allBreadcrumbs = useMemo(() => {
    const items: { label: string; deselect: () => void }[] = [];

    const getSaleLabel = (value: string) => resolveSaleLabel(saleBundle, value);

    state.facetBreadcrumbs
      ?.filter((facet) => facet.field !== 'ec_category')
      .forEach((facet) =>
        facet.values.forEach((breadcrumb) => {
          const displayValue =
            facet.field === 'ec_sale_filters'
              ? getSaleLabel(breadcrumb.value.value)
              : breadcrumb.value.value;
          items.push({ label: displayValue, deselect: breadcrumb.deselect });
        })
      );

    state.numericFacetBreadcrumbs?.forEach((facet) =>
      facet.values.forEach((breadcrumb) => {
        const rangeLabel = getNumericRangeLabel(
          breadcrumb.value.start,
          breadcrumb.value.end
        );
        items.push({ label: rangeLabel, deselect: breadcrumb.deselect });
      })
    );

    return items;
  }, [state.facetBreadcrumbs, state.numericFacetBreadcrumbs, saleBundle]);

  const handleClearAll = useCallback(() => {
    controller.deselectAll();
    clearOwnershipFilter();
  }, [controller]);

  const visibleChips = allBreadcrumbs.slice(0, MAX_VISIBLE_CHIPS);
  const moreCount = allBreadcrumbs.length - MAX_VISIBLE_CHIPS;

  return (
    <div className={c('flex flex-col gap-3', className)}>
      {/* SORT AND FILTER button */}
      <button
        type="button"
        onClick={() => setIsDrawerOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={isDrawerOpen}
        data-test="mobile-sort-and-filter-button"
        className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-grey-600 py-2.5 text-sm font-semibold uppercase tracking-wider text-grey-800"
      >
        <Faders size={16} />
        {getLabel('search.sort-and-filter', 'Sort and Filter')}
      </button>

      {/* Active filter chips */}
      {allBreadcrumbs.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-2"
          role="group"
          aria-label={getLabel('search.breadcrumbs.label', 'Active filters')}
        >
          {visibleChips.map((chip, index) => (
            <button
              key={`${chip.label}-${index}`}
              type="button"
              onClick={chip.deselect}
              aria-label={`Remove ${chip.label} filter`}
              className="flex items-center gap-1 rounded-full bg-grey-100 py-1 pl-3 pr-2 text-sm font-medium leading-5 text-grey-700"
            >
              {chip.label}
              <XCircle size={16} className="shrink-0 text-grey-500" />
            </button>
          ))}

          {moreCount > 0 && (
            <span className="text-sm text-grey-500">{moreCount} more</span>
          )}

          <button
            type="button"
            onClick={handleClearAll}
            className="text-sm text-grey-500 hover:text-grey-700"
          >
            {getLabel('search.breadcrumbs.clear', 'Clear')}
          </button>
        </div>
      )}

      <MobileFilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </div>
  );
};
